import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  CreateProductInput,
  ProductListItem,
  ProductListQuery,
  ProductView,
  UpdateProductInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

const KEY = ['products'] as const;

export function useProducts(query: Partial<ProductListQuery>) {
  return useQuery({
    queryKey: [...KEY, 'list', query],
    queryFn: async (): Promise<{
      data: ProductListItem[];
      meta: { total: number; page: number; pageSize: number };
    }> => {
      const res = await apiClient.get<{
        data: ProductListItem[];
        meta: { total: number; page: number; pageSize: number };
      }>('/products', { params: query });
      return res.data;
    },
  });
}

export function useProduct(id: number | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: id != null,
    queryFn: async (): Promise<ProductView> => {
      const res = await apiClient.get<ApiSuccess<ProductView>>(`/products/${id}`);
      return res.data.data;
    },
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProductInput): Promise<ProductView> => {
      const res = await apiClient.post<ApiSuccess<ProductView>>('/products', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateProduct(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProductInput): Promise<ProductView> => {
      const res = await apiClient.patch<ApiSuccess<ProductView>>(`/products/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/products/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
